import { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import type { ConnectionCancellationPolicies } from '@luxuryescapes/lib-refunds'
import { formatDate } from '@luxuryescapes/lib-refunds'

import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import Clickable from 'components/Common/Clickable'
import BodyText from 'LuxKit/Typography/BodyText'
import LineRefundIcon from 'LuxKit/Icons/line/LineRefundIcon'
import SolidShieldCheckIcon from 'LuxKit/Icons/solid/SolidShieldCheckIcon'
import TextLink from 'LuxKit/TextLink'
import { Refundable, getRefundDescriptions } from '../utils/getCancellationPolicyStatus'
import { getConnectionRefundPolicyDetails } from '../utils/getAlwaysOnConnectionRefundPolicyDetails'
import ConnectionCancellationPolicyModal from './ConnectionCancellationPolicyModal'
import ModalContext from 'contexts/ModalContext'

const MiddledTooltipIcon = styled(LineInfoCircleIcon)`
  vertical-align: middle;
`

interface Props {
  cancellationPolicies: ConnectionCancellationPolicies
  cancellationPolicy?: App.OfferCancellationPolicy
  checkIn?: string
  className?: string
  hideNonRefundableMessage?: boolean
  textLinkFormat?: boolean
  hideExcludesFlights?: boolean
  offerType?: string
}

function ConnectionCancellationPoliciesComponent(props: Props) {
  const { cancellationPolicies, cancellationPolicy, className, checkIn, hideNonRefundableMessage, textLinkFormat, hideExcludesFlights, offerType } = props
  const intl = useIntl()
  const { deadline, partiallyRefundableUntil, refundable, timezone } = getConnectionRefundPolicyDetails(cancellationPolicies, checkIn)
  const isRental = offerType === 'rental'
  const refundableIcon = isRental ? <SolidShieldCheckIcon /> : <LineRefundIcon />

  const refundDescriptions = useMemo(() => getRefundDescriptions(offerType, intl), [offerType, intl])

  const partiallyRefundableLabel = intl.formatMessage({
    id: 'KIcFv84yrcW6EUU6GMiE',
    defaultMessage: 'Partially refundable',
    description: 'Label for partially refundable cancellation policy on offer tile',
  })

  const freeCancellationAvailableLabel = intl.formatMessage({
    id: 'Ih80cS-Z5Yp1RT31VoFD',
    defaultMessage: 'Free cancellation available',
    description: 'Label for free cancellation policy when non-refundable message is hidden',
  })

  const untilDeadlineHotelOnlyLabel = deadline ? intl.formatMessage(
    {
      id: 'wfLIxj6fYkQqGtxBYAX2',
      defaultMessage: 'until {date} (Hotel only)',
      description: 'Cancellation deadline date with hotel-only qualifier',
    },
    { date: formatDate.formattedShortDateByTimezone(deadline, timezone) },
  ) : undefined

  const untilPartialDeadlineHotelOnlyLabel = partiallyRefundableUntil ? intl.formatMessage(
    {
      id: 'wfLIxj6fYkQqGtxBYAX2',
      defaultMessage: 'until {date} (Hotel only)',
      description: 'Cancellation deadline date with hotel-only qualifier',
    },
    { date: formatDate.formattedShortDateByTimezone(partiallyRefundableUntil, timezone) },
  ) : undefined

  const showModal = useContext(ModalContext)

  const onClickIcon = useCallback(() => {
    showModal(<ConnectionCancellationPolicyModal
      title={refundDescriptions[refundable]}
      hideExcludesFlights={!!hideExcludesFlights}
      cancellationPolicyLines={cancellationPolicies.description}
      cancellationPolicyType={cancellationPolicy?.type}
    />)
  }, [showModal, refundable, refundDescriptions, hideExcludesFlights, cancellationPolicies, cancellationPolicy])

  return (
    <Clickable onClick={onClickIcon} className={className} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      {!hideNonRefundableMessage && (
        <>
          {refundable === Refundable.Fully && (
            <>
              {textLinkFormat &&
                <TextLink
                  size="medium"
                  colour="highlight-primary-dark"
                  underline={false}
                  startIcon={refundableIcon}
                >
                  {refundDescriptions[refundable]}
                </TextLink>}
              {!textLinkFormat && (
                <>
                  <BodyText variant="medium" weight="bold" colour="highlight-primary-dark" as="span">{refundDescriptions[refundable]}</BodyText>
                  {deadline &&
                    <BodyText variant="medium" as="span">{untilDeadlineHotelOnlyLabel}</BodyText>}
                  <MiddledTooltipIcon size="XS" />
                </>
              )}
            </>
          )}
          {refundable === Refundable.Partial && (
            <>
              {textLinkFormat &&
                <TextLink
                  size="medium"
                  colour="highlight-primary-dark"
                  underline={false}
                  startIcon={refundableIcon}
                >
                  {refundDescriptions[refundable]}
                </TextLink>}
              {!textLinkFormat && (
                <>
                  <BodyText variant="medium" weight="bold" colour="highlight-primary-dark" as="span">{partiallyRefundableLabel}</BodyText>
                  {partiallyRefundableUntil &&
                    <BodyText variant="medium" as="span">{untilPartialDeadlineHotelOnlyLabel}</BodyText>}
                  <MiddledTooltipIcon size="XS" />
                </>
              )}
            </>
          )}
          {refundable === Refundable.Non && (
            <>
              <BodyText variant="medium" as="span">{refundDescriptions[refundable]}</BodyText>
              <MiddledTooltipIcon size="XS" />
            </>
          )}
        </>
      )}
      {hideNonRefundableMessage && refundable === Refundable.Fully && (
        <>
          {textLinkFormat &&
            <TextLink
              size="medium"
              colour="highlight-primary-dark"
              underline={false}
              startIcon={<LineRefundIcon />}
            >
              {freeCancellationAvailableLabel}
            </TextLink>}
          {!textLinkFormat && (
            <>
              <BodyText variant="medium" weight="bold" colour="highlight-primary-dark" as="span">{freeCancellationAvailableLabel}</BodyText>
              {deadline &&
                <BodyText variant="medium" as="span"> {untilDeadlineHotelOnlyLabel}</BodyText>}
              <MiddledTooltipIcon size="XS" />
            </>
          )}
        </>
      )}
    </Clickable>
  )
}

export default ConnectionCancellationPoliciesComponent
